import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  fetchPropBotConnectedAccounts,
  syncPropBotAccountBalances,
  clearPropBotCache,
  getCacheStats
} from '@/services/admin/prop-bot-detail-admin-service';

export interface PropBotConnectedAccount {
  id: string;
  botId: string;
  tradingAccountId: string;
  volumeMultiplier: number;
  isReversePosition: boolean;
  status: string;
  addedDate: string;
  // User info
  userId: string;
  userName: string;
  userEmail: string;
  userAvatar?: string;
  // Account info
  accountNumber: string;
  balance: number | null;
  isLive: boolean;
  broker?: string;
  propFirm?: string;
  lastBalanceUpdate?: string;
}

export interface PropBotAccountsStats {
  totalAccounts: number;
  uniqueUsers: number;
  totalCapital: number;
  reversePositions: number;
  liveAccounts: number;
  demoAccounts: number;
}

export const usePropBotDetailAdmin = (botId: string | undefined) => {
  const queryClient = useQueryClient();

  const {
    data: connectedAccounts = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['propBotConnectedAccounts', botId],
    queryFn: () => fetchPropBotConnectedAccounts(botId!),
    enabled: !!botId,
  });

  const syncBalancesMutation = useMutation({
    mutationFn: (propBotId: string) => syncPropBotAccountBalances(propBotId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['propBotConnectedAccounts', botId] });
      return result;
    },
  });

  const clearCacheMutation = useMutation({
    mutationFn: () => {
      clearPropBotCache(botId);
      return Promise.resolve();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['propBotConnectedAccounts', botId] });
    },
  });

  // Enhanced stats with additional metrics
  const accountsStats: PropBotAccountsStats = React.useMemo(() => {
    if (!connectedAccounts || connectedAccounts.length === 0) {
      return {
        totalAccounts: 0,
        uniqueUsers: 0,
        totalCapital: 0,
        reversePositions: 0,
        liveAccounts: 0,
        demoAccounts: 0,
      };
    }

    const uniqueUserIds = new Set(connectedAccounts.map(acc => acc.userId));
    const totalCapital = connectedAccounts.reduce((sum, acc) => sum + (acc.balance || 0), 0);
    const reversePositions = connectedAccounts.filter(acc => acc.isReversePosition).length;
    const liveAccounts = connectedAccounts.filter(acc => acc.isLive).length;
    const demoAccounts = connectedAccounts.filter(acc => !acc.isLive).length;

    return {
      totalAccounts: connectedAccounts.length,
      uniqueUsers: uniqueUserIds.size,
      totalCapital,
      reversePositions,
      liveAccounts,
      demoAccounts,
    };
  }, [connectedAccounts]);

  // Enhanced refresh with cache clearing
  const forceRefresh = React.useCallback(async () => {
    clearPropBotCache(botId);
    return refetch();
  }, [botId, refetch]);

  return {
    connectedAccounts,
    accountsStats,
    isLoading,
    error,
    refetch,
    forceRefresh,
    syncBalances: syncBalancesMutation.mutateAsync,
    isSyncingBalances: syncBalancesMutation.isPending,
    syncResult: syncBalancesMutation.data,
    clearCache: clearCacheMutation.mutateAsync,
    isClearingCache: clearCacheMutation.isPending,
    cacheStats: getCacheStats(),
  };
};